import React from 'react';
import { Modal, Checkbox, Button } from 'antd';
import { AirflowTaskInstance } from '../../types';
import { AffectedTasksTable } from '../../affected-tasks-table';
import { useClearTaskDialog } from './use-clear-task-dialog';

type ClearTaskDialogProps = {
    dagId: string;
    task: AirflowTaskInstance;
    onClose: () => void;
    onCleared: () => void;
};

/*
 * Confirms clearing a task instance (optionally past/future/upstream/downstream/only-failed),
 * with a live dry-run preview of every task instance that would be affected
 */
export function ClearTaskDialog({ dagId, task, onClose, onCleared }: ClearTaskDialogProps) {
    const { options, handleOptionChange, affectedTasks, isLoadingPreview, isClearing, handleConfirm } = useClearTaskDialog(dagId, task, onClose, onCleared);

    return (
        <Modal
            visible
            className="airflowDashboardRoot"
            title={`Clear Task "${task.task_id}"`}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="confirm" type="danger" onClick={handleConfirm} disabled={isClearing || affectedTasks.length === 0}>
                    {isClearing ? 'Clearing…' : 'Confirm'}
                </Button>,
            ]}
        >
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <Checkbox checked={options.includePast} onChange={(e) => handleOptionChange('includePast', e.target.checked)}>
                    Past
                </Checkbox>
                <Checkbox checked={options.includeFuture} onChange={(e) => handleOptionChange('includeFuture', e.target.checked)}>
                    Future
                </Checkbox>
                <Checkbox checked={options.includeUpstream} onChange={(e) => handleOptionChange('includeUpstream', e.target.checked)}>
                    Upstream
                </Checkbox>
                <Checkbox checked={options.includeDownstream} onChange={(e) => handleOptionChange('includeDownstream', e.target.checked)}>
                    Downstream
                </Checkbox>
                <Checkbox checked={options.onlyFailed} onChange={(e) => handleOptionChange('onlyFailed', e.target.checked)}>
                    Clear only failed tasks
                </Checkbox>
                <Checkbox checked={options.preventRerun} onChange={(e) => handleOptionChange('preventRerun', e.target.checked)}>
                    Prevent rerun if task is running
                </Checkbox>
            </div>
            <AffectedTasksTable tasks={affectedTasks} isLoading={isLoadingPreview} />
        </Modal>
    );
}
